"use client";

import { useEffect, useState } from "react";
import {
  ErrorNotice,
  Notice,
  PillGroup,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  HINT,
  RESULT_CARD,
  TABLE,
  TD,
  TH,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { formatDateTime } from "@/lib/format-number";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseIntegerInput } from "@/lib/parse-number";
import {
  civilFromDays,
  dayOfYear,
  daysFromCivil,
  daysInYear,
  dayToUtcDate,
  formatIsoWeekDate,
  isoWeek,
  MAX_YEAR,
  MIN_YEAR,
  parseIsoDate,
  toIsoDate,
  usWeek,
  type WeekSpan,
  type WeekSystem,
  weekSpan,
  weeksInYear,
  weeksOfYear,
} from "@/lib/tool-kit/converters/week-number";

/** English strings. A translated content file overrides these through `ui`. */
export const WEEK_NUMBER_CALCULATOR_STRINGS = {
  system: "Week numbering",
  iso: "ISO 8601 — weeks start on Monday",
  us: "US — weeks start on Sunday, week 1 holds 1 January",
  dateLabel: "Date",
  today: "Today",
  isoWeekLabel: "ISO week number",
  isoWeekYearLabel: "ISO week-year",
  isoWeekDateLabel: "ISO week date",
  usWeekLabel: "US week number",
  dayOfYearLabel: "Day of the year",
  dayOfYearValue: "{day} of {total}",
  daysLeftLabel: "Days left in the year",
  isoRange: "ISO week {week} of {year} runs from {start} to {end}.",
  usRange: "US week {week} of {year} runs from {start} to {end}.",
  usSplit:
    "The full Sunday–Saturday week runs from {start} to {end}, but US numbering restarts on 1 January, so the days on the other side of New Year belong to another year's week.",
  boundaryNote:
    "This date falls in week {week} of {weekYear}, not {year}: an ISO week belongs to the year that contains its Thursday.",
  isoWeekCount:
    "one: {year} has {count} ISO week.|other: {year} has {count} ISO weeks.",
  usWeekCount:
    "one: {year} has {count} US week.|other: {year} has {count} US weeks.",
  pickDate: "Pick a date to see its week number.",
  invalidDate: "Choose a real date between the years 1583 and 9999.",
  lookupHeading: "Dates of a week number",
  yearLabel: "Year",
  weekLabel: "Week number",
  weekOutOfRange: "{year} has weeks 1 to {count} in this numbering.",
  yearOutOfRange: "Enter a year from 1583 to 9999.",
  enterWeek: "Enter a year and a week number.",
  tableHeading: "Every week of {year}",
  tableHeadingEmpty: "Every week of a year",
  tableYearLabel: "Show the weeks of",
  weekColumn: "Week",
  startsColumn: "Starts",
  endsColumn: "Ends",
  daysColumn: "Days",
  isoTableNote:
    "ISO weeks always have seven days. Week 1 can start in late December and the last week can end in early January.",
  usTableNote:
    "US weeks are cut at 1 January and 31 December, so the first and last week of the year are usually shorter than seven days.",
} as const;

type Strings = typeof WEEK_NUMBER_CALCULATOR_STRINGS;
type Fmt = (value: number, options?: Intl.NumberFormatOptions) => string;

const LONG_DATE: Intl.DateTimeFormatOptions = {
  weekday: "long",
  day: "numeric",
  month: "long",
  year: "numeric",
  timeZone: "UTC",
};

const SHORT_DATE: Intl.DateTimeFormatOptions = {
  weekday: "short",
  day: "numeric",
  month: "short",
  year: "numeric",
  timeZone: "UTC",
};

function useDateFormat() {
  const locale = useToolLocale();
  return (days: number) =>
    formatDateTime(locale, dayToUtcDate(days), SHORT_DATE);
}

function readYear(text: string): number | null {
  const year = parseIntegerInput(text);
  if (year === null || year < MIN_YEAR || year > MAX_YEAR) {
    return null;
  }
  return year;
}

interface DateFacts {
  iso: ReturnType<typeof isoWeek>;
  usWeekNumber: number;
  year: number;
  span: WeekSpan;
}

function readDateFacts(days: number, system: WeekSystem): DateFacts {
  const iso = isoWeek(days);
  const us = usWeek(days);
  const { year } = civilFromDays(days);
  const span =
    system === "iso"
      ? weekSpan("iso", iso.weekYear, iso.week)
      : weekSpan("us", year, us.week);
  return { iso, usWeekNumber: us.week, year, span };
}

/** The sentences under the numbers: the week's dates, then whatever is unusual about it. */
function dateNotes(
  facts: DateFacts,
  system: WeekSystem,
  strings: Strings,
  format: { fmt: Fmt; day: (days: number) => string; locale: string }
): string[] {
  const { iso, year, span } = facts;
  const { fmt, day, locale } = format;
  const notes = [
    t(system === "iso" ? strings.isoRange : strings.usRange, {
      week: fmt(span.week),
      year: String(system === "iso" ? iso.weekYear : year),
      start: day(span.start),
      end: day(span.end),
    }),
  ];
  const clipped = span.start !== span.fullStart || span.end !== span.fullEnd;
  if (system === "us" && clipped) {
    notes.push(
      t(strings.usSplit, { start: day(span.fullStart), end: day(span.fullEnd) })
    );
  }
  if (system === "iso" && iso.weekYear !== year) {
    notes.push(
      t(strings.boundaryNote, {
        week: fmt(iso.week),
        weekYear: String(iso.weekYear),
        year: String(year),
      })
    );
  }
  const countYear = system === "iso" ? iso.weekYear : year;
  const count = weeksInYear(system, countYear);
  notes.push(
    tPlural(
      system === "iso" ? strings.isoWeekCount : strings.usWeekCount,
      locale,
      count,
      { count: fmt(count), year: String(countYear) }
    )
  );
  return notes;
}

function DateResult({
  days,
  system,
  strings,
}: {
  days: number;
  system: WeekSystem;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const locale = useToolLocale();
  const formatDay = useDateFormat();
  const facts = readDateFacts(days, system);
  const ordinal = dayOfYear(days);
  const total = daysInYear(facts.year);
  const [range, ...notes] = dateNotes(facts, system, strings, {
    fmt,
    day: formatDay,
    locale,
  });
  return (
    <section aria-live="polite" className={`${RESULT_CARD} space-y-4`}>
      <h2 className={WIDGET_H2}>
        {formatDateTime(locale, dayToUtcDate(days), LONG_DATE)}
      </h2>
      <StatGrid>
        <StatCard
          emphasis={system === "iso"}
          label={strings.isoWeekLabel}
          value={fmt(facts.iso.week)}
        />
        <StatCard
          label={strings.isoWeekYearLabel}
          value={String(facts.iso.weekYear)}
        />
        <StatCard
          label={strings.isoWeekDateLabel}
          value={formatIsoWeekDate(facts.iso)}
        />
        <StatCard
          emphasis={system === "us"}
          label={strings.usWeekLabel}
          value={fmt(facts.usWeekNumber)}
        />
        <StatCard
          label={strings.dayOfYearLabel}
          value={t(strings.dayOfYearValue, {
            day: fmt(ordinal),
            total: fmt(total),
          })}
        />
        <StatCard label={strings.daysLeftLabel} value={fmt(total - ordinal)} />
      </StatGrid>
      <div className="space-y-2 text-sm leading-relaxed">
        <p className="text-foreground">{range}</p>
        {notes.map((note) => (
          <p className="text-muted-foreground" key={note}>
            {note}
          </p>
        ))}
      </div>
    </section>
  );
}

function WeekLookup({
  system,
  strings,
  year,
  week,
  onYear,
  onWeek,
}: {
  system: WeekSystem;
  strings: Strings;
  year: string;
  week: string;
  onYear: (value: string) => void;
  onWeek: (value: string) => void;
}) {
  const fmt = useToolNumber();
  const formatDay = useDateFormat();
  const yearValue = readYear(year);
  const weekValue = parseIntegerInput(week);
  let message: { error: boolean; text: string } | null = null;
  if (year.trim() === "" || week.trim() === "") {
    message = { error: false, text: strings.enterWeek };
  } else if (yearValue === null) {
    message = { error: true, text: strings.yearOutOfRange };
  } else {
    const max = weeksInYear(system, yearValue);
    if (weekValue === null || weekValue < 1 || weekValue > max) {
      message = {
        error: true,
        text: t(strings.weekOutOfRange, {
          count: fmt(max),
          year: String(yearValue),
        }),
      };
    } else {
      const span = weekSpan(system, yearValue, weekValue);
      message = {
        error: false,
        text: t(system === "iso" ? strings.isoRange : strings.usRange, {
          week: fmt(weekValue),
          year: String(yearValue),
          start: formatDay(span.start),
          end: formatDay(span.end),
        }),
      };
    }
  }
  return (
    <section className={`${CARD} space-y-4`}>
      <h2 className={WIDGET_H2}>{strings.lookupHeading}</h2>
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          id="wn-lookup-year"
          label={strings.yearLabel}
          numeric
          onChange={onYear}
          value={year}
        />
        <TextField
          id="wn-lookup-week"
          label={strings.weekLabel}
          numeric
          onChange={onWeek}
          value={week}
        />
      </div>
      <div aria-live="polite">
        {message?.error ? <ErrorNotice>{message.text}</ErrorNotice> : null}
        {message && !message.error ? (
          <p className="text-foreground text-sm">{message.text}</p>
        ) : null}
      </div>
    </section>
  );
}

function WeekTable({
  system,
  strings,
  year,
  selected,
}: {
  system: WeekSystem;
  strings: Strings;
  year: number;
  selected: number | null;
}) {
  const fmt = useToolNumber();
  const formatDay = useDateFormat();
  const weeks: WeekSpan[] = weeksOfYear(system, year);
  return (
    <div className="space-y-2">
      <div className="max-h-[520px] overflow-auto rounded-lg border border-border bg-card">
        <table className={TABLE}>
          <thead>
            <tr>
              <th className={TH} scope="col">
                {strings.weekColumn}
              </th>
              <th className={TH} scope="col">
                {strings.startsColumn}
              </th>
              <th className={TH} scope="col">
                {strings.endsColumn}
              </th>
              <th className={TH} scope="col">
                {strings.daysColumn}
              </th>
            </tr>
          </thead>
          <tbody>
            {weeks.map((span) => {
              const isSelected =
                selected !== null &&
                selected >= span.start &&
                selected <= span.end;
              return (
                <tr
                  aria-current={isSelected ? "date" : undefined}
                  className={isSelected ? "bg-primary/10 font-medium" : ""}
                  key={span.week}
                >
                  <td className={TD}>{fmt(span.week)}</td>
                  <td className={TD}>{formatDay(span.start)}</td>
                  <td className={TD}>{formatDay(span.end)}</td>
                  <td className={TD}>{fmt(span.end - span.start + 1)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className={HINT}>
        {system === "iso" ? strings.isoTableNote : strings.usTableNote}
      </p>
    </div>
  );
}

/** Today's calendar date on the reader's own clock — called from effects and handlers only. */
function todayDays(): number {
  const now = new Date();
  return daysFromCivil(now.getFullYear(), now.getMonth() + 1, now.getDate());
}

export default function WeekNumberCalculatorWidget() {
  const strings = useToolUi(WEEK_NUMBER_CALCULATOR_STRINGS);
  const [system, setSystem] = useState<WeekSystem>("iso");
  const [dateText, setDateText] = useState("");
  const [lookupYear, setLookupYear] = useState("");
  const [lookupWeek, setLookupWeek] = useState("");
  const [tableYear, setTableYear] = useState("");

  const tableYearFor = (days: number, numbering: WeekSystem) =>
    String(
      numbering === "iso" ? isoWeek(days).weekYear : civilFromDays(days).year
    );

  // Today is only known in the browser, so it arrives after hydration.
  useEffect(() => {
    const today = todayDays();
    const week = isoWeek(today);
    setDateText((current) => current || toIsoDate(today));
    setLookupYear((current) => current || String(week.weekYear));
    setLookupWeek((current) => current || String(week.week));
    setTableYear((current) => current || String(week.weekYear));
  }, []);

  const days = parseIsoDate(dateText);
  const tableYearValue = readYear(tableYear);

  function changeDate(value: string) {
    setDateText(value);
    const parsed = parseIsoDate(value);
    if (parsed !== null) {
      setTableYear(tableYearFor(parsed, system));
    }
  }

  function changeSystem(value: WeekSystem) {
    setSystem(value);
    if (days !== null) {
      setTableYear(tableYearFor(days, value));
    }
  }

  return (
    <div className="space-y-6">
      <PillGroup
        label={strings.system}
        name="wn-system"
        onChange={changeSystem}
        options={[
          { value: "iso", label: strings.iso },
          { value: "us", label: strings.us },
        ]}
        value={system}
      />
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[220px] flex-1">
          <TextField
            id="wn-date"
            label={strings.dateLabel}
            onChange={changeDate}
            type="date"
            value={dateText}
          />
        </div>
        <button
          className={BUTTON_SECONDARY}
          onClick={() => changeDate(toIsoDate(todayDays()))}
          type="button"
        >
          {strings.today}
        </button>
      </div>
      {days === null && dateText !== "" ? (
        <ErrorNotice>{strings.invalidDate}</ErrorNotice>
      ) : null}
      {days === null && dateText === "" ? (
        <Notice>{strings.pickDate}</Notice>
      ) : null}
      {days === null ? null : (
        <DateResult days={days} strings={strings} system={system} />
      )}
      <WeekLookup
        onWeek={setLookupWeek}
        onYear={setLookupYear}
        strings={strings}
        system={system}
        week={lookupWeek}
        year={lookupYear}
      />
      <section className={`${CARD} space-y-4`}>
        <h2 className={WIDGET_H2}>
          {tableYearValue === null
            ? strings.tableHeadingEmpty
            : t(strings.tableHeading, { year: String(tableYearValue) })}
        </h2>
        <div className="max-w-[220px]">
          <TextField
            id="wn-table-year"
            label={strings.tableYearLabel}
            numeric
            onChange={setTableYear}
            value={tableYear}
          />
        </div>
        {tableYearValue === null ? (
          <Notice>{strings.yearOutOfRange}</Notice>
        ) : (
          <WeekTable
            selected={days}
            strings={strings}
            system={system}
            year={tableYearValue}
          />
        )}
      </section>
    </div>
  );
}
